/**
 * Static UI-string translations for public pages that mix dynamic
 * (DB-overlay) content with hard-coded English chrome. Small enough not
 * to warrant a full i18next dependency, and the ES copy sits alongside
 * EN in one place so translators can update in a single PR.
 *
 * Pattern: `const t = useT();` then `<h1>{t("about.hero.title")}</h1>`.
 * Missing keys fall back to the English string (never blows up).
 */
import { useLang } from "@/context/LangContext";

// Nested dict, key path resolved with `.` — keeps sections readable.
export const STRINGS = {
  en: {
    common: {
      loading: "Loading...",
      not_found: "Not found.",
      back: "Back",
      view_details: "View details",
      more: "More",
    },
    nf: {
      eyebrow: "Page not found",
      title: "This page has moved on.",
      body: "The page you were looking for doesn't exist or has been relocated. You can return to the homepage, or continue to our current legislative work.",
      home: "Return home",
      bills: "Legislative work",
    },
    about: {
      eyebrow: "About",
      hero_title: "The Catholic voice at the Colorado Capitol.",
      hero_body: "Basing its mission on the Gospel of Jesus Christ, as expressed in Catholic social teaching and the consistent life ethic, the CCC works to ensure a dignified and productive life for all Coloradans and provide opportunities for Catholics in Colorado to carry out their moral responsibility to participate in political life.",
      mission_title: "Mission",
      mission_body: "The Colorado Catholic Conference is the united voice of the Catholic bishops of Colorado and serves to uphold the dignity of human life and the common good in public policy.",
      mission_intro: "The CCC advances policy at all levels of government — local, state, and federal — on issues that impact the Catholic Church and its teachings. The CCC's work includes:",
      mission_list: [
        "Legislative Advocacy and Government Relations",
        "Coalition Building and Grassroots Mobilization",
        "Education and Leadership Development",
      ],
      board_eyebrow: "Board of Governors",
      board_title: "The Bishops of Colorado.",
      staff_eyebrow: "Staff",
      staff_title: "Meet the team.",
      partners_eyebrow: "Partners",
      partners_title: "Our diocesan partners.",
    },
    events: {
      eyebrow: "Events",
      hero_title: "Gatherings & Formation.",
      hero_body: "Convenings, prayer, and formation events across Colorado. RSVP to save your seat — we'll send reminders at 48 hours and 2 hours before the event.",
      empty: "No upcoming events. Check back soon.",
      rsvp_button: "RSVP",
      when: "When",
      where: "Where",
      details: "Details",
    },
    voter_guide: {
      eyebrow: "Voter Guide",
      hero_title: "Vote your conscience, form your conscience.",
      empty: "The next voter guide will be published closer to the election. Sign up for our newsletter to be notified.",
      intro_label: "Introduction",
      ballot_label: "On the ballot this cycle",
      formation_label: "Forming your conscience",
    },
    bills: {
      eyebrow: "Legislative Work",
      hero_title: "Bill Tracker.",
      hero_body: "Every bill the Colorado Catholic Conference is tracking, with our position, current status, and why it matters for Catholics in Colorado.",
      search_placeholder: "Search by title or bill number...",
      filter_position: "Position",
      filter_stage: "Stage",
      filter_all_issues: "All Issues",
      empty: "No bills match these filters.",
      next_hearing: "Next hearing",
      why_it_matters: "Why this matters",
      summary_label: "Summary",
      committee_hearing: "Committee & Hearing",
      back_to_tracker: "Back to bill tracker",
      cst_eyebrow: "Catholic Social Teaching",
      cst_heading: "Why the Conference speaks on",
      take_action_eyebrow: "Take Action",
      make_voice_heard: "Make your voice heard on",
      view_related_alerts: "View Related Action Alerts",
      bill_not_found: "Bill not found.",
    },
    statements: {
      eyebrow: "Resources",
      hero_title: "Statements & Letters.",
      hero_body: "The complete archive of Bishops' Letters, Conference Statements, and Testimony filed by the Colorado Catholic Conference.",
      search_placeholder: "Search...",
      type_all: "All",
      library_link: "Library",
      not_found: "Not found.",
      by_author: "By",
    },
    alerts: {
      eyebrow: "Take Action",
      hero_title: "Action Alert Center.",
      hero_body: "Contact your legislator in minutes. Each alert includes talking points and a pre-filled message you can edit and send.",
      take_action_arrow: "Take Action →",
      all_alerts_link: "All Action Alerts",
      taken_action_before: "Catholics have taken action.",
      overview: "Overview",
      talking_points: "Talking Points",
      related_bill: "Related Bill",
      parish_toolkit: "Parish Toolkit",
      toolkit_description: "One-page PDFs to share this alert from the pulpit and the bulletin.",
      homily_aid: "Homily Aid (Letter, PDF)",
      bulletin_insert: "Bulletin Insert (Half, PDF)",
      email_to_priest: "Email this to a priest",
      imprimatur: "Imprimatur — Bishops of Colorado",
      step_of_two: "Step {n} of 2",
      find_legislators_title: "Find your legislators.",
      find_legislators_body: "Enter your Colorado ZIP code and we'll show you which state legislators represent you.",
      zip_placeholder: "ZIP code",
      find_legislators_button: "Find My Legislators",
      send_message_title: "Send your message.",
      your_legislators_label: "Your legislators",
      your_name: "Your name",
      your_email: "Your email",
      subject: "Subject",
      send_via_mail_client: "Open in my email",
      send_via_ccc: "Send via CCC (reply-to me)",
      send_message_button: "Send Message",
      err_no_legislator: "Select at least one legislator",
      err_no_zip: "Please enter your ZIP code",
      err_lookup_fail: "Lookup failed",
      err_submit_fail: "Submission failed",
      success_recorded: "Your voice has been recorded. Thank you.",
      thank_you: "Thank you.",
      thank_you_body: "Your action has been recorded. We'll follow up with the outcome as this campaign develops.",
    },
    assistant: {
      title_bill: "Ask about a bill",
      title_record: "Search the record",
      eyebrow_bill: "Legislative Research",
      eyebrow_record: "Reference",
      description_bill: "A working research tool. Type a bill number or ask what the Conference has said. Answers pull from the record on this site.",
      description_record: "Type a question and get a plain-English answer drawn from the Conference's published bills, statements, and priorities.",
      placeholder_bill: "Type a bill number, a topic, or a question.",
      placeholder_record: "Type a topic, a bill, or a question about our work.",
      suggestions_bill: [
        "What is the Conference's position on this bill?",
        "When is the next hearing?",
        "Who introduced it and what committee is it in?",
      ],
      suggestions_record: [
        "What has the Conference said about this priority?",
        "Which bills does this priority touch this session?",
        "Where can I find the bishops' most recent statement?",
      ],
      try_one: "Try one of these",
      send: "Ask",
      offline: "The reference desk is offline.",
      rate_limited: "You've asked a lot of questions in a short time — please wait a moment before your next one.",
      network_error: "The reference desk isn't responding. Please try again shortly.",
      stay_in_loop: "Stay in the loop",
      updates_prompt: "Would you like updates from the Conference?",
      name_placeholder: "Your name",
      email_placeholder: "Email",
      parish_placeholder: "Parish (optional)",
      sign_up: "Sign me up",
      signing_up: "Saving…",
    },
    misc: {
      captcha_required: "Please complete the captcha",
      generic_fail: "Failed",
    },
    join: {
      eyebrow: "Take Action",
      hero_title: "Join the Catholic Advocacy Network.",
      hero_body: "Join the Colorado Catholic Conference Network to learn more about our work and how you can do your part in shaping the moral character of our community through the political process.",
      full_name: "Full name",
      email: "Email",
      zip: "ZIP",
      parish: "Parish",
      interests_label: "Issue interests (optional)",
      submit: "Join the Network",
      welcome_title: "Welcome.",
      welcome_body: "Watch your inbox for the first email in our welcome series.",
      toast_welcome: "Welcome!",
    },
    contact: {
      eyebrow: "Contact",
      hero_title: "Get in touch.",
      address_line: "Colorado Catholic Conference · 1535 Logan St · Denver, CO 80203 · info@cocatholic.org",
      name: "Name",
      email: "Email",
      subject: "Subject",
      message: "Message",
      submit: "Send Message",
      thanks: "Thank you. We'll be in touch.",
    },
    legislator: {
      eyebrow: "Take Action",
      hero_title: "Find My Legislator.",
      hero_body: "Enter your Colorado ZIP code to see who represents you at the State Capitol.",
      zip_placeholder: "ZIP code",
      submit: "Find",
      committees_label: "Committees",
    },
    jobs: {
      eyebrow: "Careers",
      hero_title: "Jobs at the CCC.",
      no_openings_prefix: "There are no open positions at this time. To submit your resume for future consideration, please email ",
    },
    resources: {
      eyebrow: "Resources",
      hero_title: "For your formation.",
      card_statements_title: "Statements & Testimony",
      card_statements_desc: "The complete archive of CCC statements and legislative testimony.",
      card_letters_title: "Bishops' Letters",
      card_letters_desc: "Pastoral letters from the four Bishops of Colorado.",
      card_voter_title: "Voter Guide",
      card_voter_desc: "How to vote as a Catholic — issue by issue.",
    },
    bishop_letters: {
      eyebrow: "Resources",
      hero_title: "Bishops' Letters.",
      empty: "No letters yet.",
    },
    priest_signature: {
      title: "Bishop signature request",
      signing_as_label: "Signing as",
      author_label: "Author",
      submit: "Request signature",
      submitting: "Sending…",
      already_completed: "This request has already been signed.",
      thanks: "Thank you — your request has been submitted.",
      generic_fail: "We couldn't submit the request. Please try again.",
    },
    send_priest: {
      title: "Send to Priest",
      description: "Send this Action Alert's Homily Aid + Bulletin Insert PDFs to a Colorado priest.",
      choose_priests: "Choose priest(s)",
      max_10_hint: "You may select up to 10 priests.",
      sender_name: "Your name",
      sender_email: "Your email",
      note_placeholder: "A short note to the priest (optional)",
      submit: "Send toolkit",
      submitting: "Sending…",
      thanks: "Thank you — the parish toolkit is on its way.",
      generic_fail: "We couldn't send the toolkit. Please try again.",
    },
    priest_portal: {
      title: "Homily Prep Digest — subscription",
      subtitle: "Manage your subscription to the Friday Homily Prep digest.",
      welcome: "Dear",
      opt_in_label: "Send me the Homily Prep digest each Friday",
      opt_in_hint: "You'll receive a short email pairing each actively-tracked bill with a Catholic Social Teaching talking point.",
      lang_label: "Language",
      lang_en: "English",
      lang_es: "Spanish (Español)",
      save: "Save preferences",
      saving: "Saving…",
      saved: "Preferences saved.",
      currently_subscribed: "You are currently subscribed.",
      currently_unsubscribed: "You are not currently subscribed.",
      link_invalid: "This link is not valid, or has expired.",
      link_invalid_body: "Please contact the CCC office to receive a fresh link.",
      generic_fail: "We couldn't save your preferences. Please try again.",
    },
    homily_archive: {
      eyebrow: "Homily Prep",
      title: "Archive of the Friday Homily Prep digest.",
      subtitle: "The Colorado Catholic Conference publishes a weekly digest pairing each actively-tracked bill with a Catholic Social Teaching talking point. Every past week is here.",
      empty: "No archived digests yet.",
      bills_label: "bills",
      back_to_list: "All digests",
      not_found: "Digest not found.",
    },
  },
  es: {
    common: {
      loading: "Cargando...",
      not_found: "No encontrado.",
      back: "Volver",
      view_details: "Ver detalles",
      more: "Más",
    },
    nf: {
      eyebrow: "Página no encontrada",
      title: "Esta página ya no está aquí.",
      body: "La página que buscaba no existe o ha sido trasladada. Puede volver a la página de inicio o continuar a nuestro trabajo legislativo actual.",
      home: "Volver al inicio",
      bills: "Trabajo legislativo",
    },
    about: {
      eyebrow: "Acerca de",
      hero_title: "La voz católica en el Capitolio de Colorado.",
      hero_body: "Basando su misión en el Evangelio de Jesucristo, expresado en la doctrina social católica y la ética de vida consistente, la CCC trabaja para garantizar una vida digna y productiva para todos los habitantes de Colorado y brindar oportunidades a los católicos de Colorado para cumplir con su responsabilidad moral de participar en la vida política.",
      mission_title: "Misión",
      mission_body: "La Conferencia Católica de Colorado es la voz unida de los obispos católicos de Colorado y sirve para defender la dignidad de la vida humana y el bien común en las políticas públicas.",
      mission_intro: "La CCC promueve políticas en todos los niveles del gobierno — local, estatal y federal — en temas que impactan a la Iglesia Católica y sus enseñanzas. El trabajo de la CCC incluye:",
      mission_list: [
        "Abogacía legislativa y relaciones gubernamentales",
        "Construcción de coaliciones y movilización de base",
        "Educación y desarrollo de liderazgo",
      ],
      board_eyebrow: "Junta de Gobernadores",
      board_title: "Los Obispos de Colorado.",
      staff_eyebrow: "Personal",
      staff_title: "Conozca al equipo.",
      partners_eyebrow: "Socios",
      partners_title: "Nuestros socios diocesanos.",
    },
    events: {
      eyebrow: "Eventos",
      hero_title: "Encuentros y Formación.",
      hero_body: "Convocatorias, oración y eventos de formación en todo Colorado. Confirme su asistencia para reservar su lugar — le enviaremos recordatorios 48 horas y 2 horas antes del evento.",
      empty: "No hay eventos próximos. Vuelva pronto.",
      rsvp_button: "Confirmar Asistencia",
      when: "Cuándo",
      where: "Dónde",
      details: "Detalles",
    },
    voter_guide: {
      eyebrow: "Guía del Votante",
      hero_title: "Vote su conciencia, forme su conciencia.",
      empty: "La próxima guía del votante se publicará más cerca de las elecciones. Suscríbase a nuestro boletín para recibir notificaciones.",
      intro_label: "Introducción",
      ballot_label: "En la boleta este ciclo",
      formation_label: "Formando su conciencia",
    },
    bills: {
      eyebrow: "Trabajo Legislativo",
      hero_title: "Rastreador de Proyectos.",
      hero_body: "Cada proyecto de ley que la Conferencia Católica de Colorado está siguiendo, con nuestra posición, el estado actual y por qué es importante para los católicos de Colorado.",
      search_placeholder: "Buscar por título o número de proyecto...",
      filter_position: "Posición",
      filter_stage: "Etapa",
      filter_all_issues: "Todos los Temas",
      empty: "Ningún proyecto coincide con estos filtros.",
      next_hearing: "Próxima audiencia",
      why_it_matters: "Por qué importa",
      summary_label: "Resumen",
      committee_hearing: "Comité y Audiencia",
      back_to_tracker: "Volver al rastreador",
      cst_eyebrow: "Doctrina Social Católica",
      cst_heading: "Por qué la Conferencia habla sobre",
      take_action_eyebrow: "Actuar",
      make_voice_heard: "Haga oír su voz sobre",
      view_related_alerts: "Ver Alertas Relacionadas",
      bill_not_found: "Proyecto no encontrado.",
    },
    statements: {
      eyebrow: "Recursos",
      hero_title: "Declaraciones y Cartas.",
      hero_body: "El archivo completo de Cartas Obispales, Declaraciones de la Conferencia y Testimonios presentados por la Conferencia Católica de Colorado.",
      search_placeholder: "Buscar...",
      type_all: "Todos",
      library_link: "Biblioteca",
      not_found: "No encontrado.",
      by_author: "Por",
    },
    alerts: {
      eyebrow: "Actuar",
      hero_title: "Centro de Alertas de Acción.",
      hero_body: "Contacte a su legislador en minutos. Cada alerta incluye puntos de discusión y un mensaje pre-redactado que puede editar y enviar.",
      take_action_arrow: "Actuar Ahora →",
      all_alerts_link: "Todas las Alertas",
      taken_action_before: "católicos han actuado.",
      overview: "Descripción",
      talking_points: "Puntos de Discusión",
      related_bill: "Proyecto Relacionado",
      parish_toolkit: "Kit Parroquial",
      toolkit_description: "PDFs de una página para compartir esta alerta desde el púlpito y el boletín.",
      homily_aid: "Ayuda para la Homilía (Carta, PDF)",
      bulletin_insert: "Inserto del Boletín (Media, PDF)",
      email_to_priest: "Enviar esto a un sacerdote",
      imprimatur: "Imprimatur — Obispos de Colorado",
      step_of_two: "Paso {n} de 2",
      find_legislators_title: "Encuentre a sus legisladores.",
      find_legislators_body: "Ingrese su código postal de Colorado y le mostraremos qué legisladores estatales le representan.",
      zip_placeholder: "Código postal",
      find_legislators_button: "Encontrar Mis Legisladores",
      send_message_title: "Envíe su mensaje.",
      your_legislators_label: "Sus legisladores",
      your_name: "Su nombre",
      your_email: "Su correo electrónico",
      subject: "Asunto",
      send_via_mail_client: "Abrir en mi correo",
      send_via_ccc: "Enviar vía CCC (respuesta a mí)",
      send_message_button: "Enviar Mensaje",
      err_no_legislator: "Seleccione al menos un legislador",
      err_no_zip: "Por favor ingrese su código postal",
      err_lookup_fail: "Búsqueda fallida",
      err_submit_fail: "Envío fallido",
      success_recorded: "Su voz ha sido registrada. Gracias.",
      thank_you: "Gracias.",
      thank_you_body: "Su acción ha sido registrada. Le informaremos del resultado a medida que se desarrolle esta campaña.",
    },
    assistant: {
      title_bill: "Pregunte sobre un proyecto",
      title_record: "Consultar el registro",
      eyebrow_bill: "Investigación Legislativa",
      eyebrow_record: "Referencia",
      description_bill: "Una herramienta de investigación. Escriba un número de proyecto o pregunte lo que ha dicho la Conferencia. Las respuestas provienen del registro de este sitio.",
      description_record: "Escriba una pregunta y reciba una respuesta clara basada en los proyectos, declaraciones y prioridades publicados de la Conferencia.",
      placeholder_bill: "Escriba un número de proyecto, un tema o una pregunta.",
      placeholder_record: "Escriba un tema, un proyecto o una pregunta sobre nuestro trabajo.",
      suggestions_bill: [
        "¿Cuál es la posición de la Conferencia sobre este proyecto?",
        "¿Cuándo es la próxima audiencia?",
        "¿Quién lo presentó y en qué comité está?",
      ],
      suggestions_record: [
        "¿Qué ha dicho la Conferencia sobre esta prioridad?",
        "¿Qué proyectos de esta sesión tocan esta prioridad?",
        "¿Dónde puedo encontrar la declaración más reciente de los obispos?",
      ],
      try_one: "Pruebe una de estas",
      send: "Preguntar",
      offline: "El escritorio de referencia está fuera de línea.",
      rate_limited: "Ha hecho muchas preguntas en poco tiempo — espere un momento antes de la siguiente.",
      network_error: "El escritorio de referencia no responde. Intente de nuevo en un momento.",
      stay_in_loop: "Manténgase informado",
      updates_prompt: "¿Le gustaría recibir actualizaciones de la Conferencia?",
      name_placeholder: "Su nombre",
      email_placeholder: "Correo electrónico",
      parish_placeholder: "Parroquia (opcional)",
      sign_up: "Suscribirme",
      signing_up: "Guardando…",
    },
    misc: {
      captcha_required: "Por favor complete el captcha",
      generic_fail: "Error",
    },
    join: {
      eyebrow: "Actuar",
      hero_title: "Únase a la Red de Defensa Católica.",
      hero_body: "Únase a la Red de la Conferencia Católica de Colorado para conocer más sobre nuestro trabajo y cómo puede contribuir a moldear el carácter moral de nuestra comunidad a través del proceso político.",
      full_name: "Nombre completo",
      email: "Correo electrónico",
      zip: "Código postal",
      parish: "Parroquia",
      interests_label: "Áreas de interés (opcional)",
      submit: "Unirse a la Red",
      welcome_title: "Bienvenido.",
      welcome_body: "Revise su bandeja de entrada para el primer correo de nuestra serie de bienvenida.",
      toast_welcome: "¡Bienvenido!",
    },
    contact: {
      eyebrow: "Contacto",
      hero_title: "Póngase en contacto.",
      address_line: "Conferencia Católica de Colorado · 1535 Logan St · Denver, CO 80203 · info@cocatholic.org",
      name: "Nombre",
      email: "Correo electrónico",
      subject: "Asunto",
      message: "Mensaje",
      submit: "Enviar mensaje",
      thanks: "Gracias. Estaremos en contacto.",
    },
    legislator: {
      eyebrow: "Actuar",
      hero_title: "Encuentre a su Legislador.",
      hero_body: "Ingrese su código postal de Colorado para ver quién le representa en el Capitolio del Estado.",
      zip_placeholder: "Código postal",
      submit: "Buscar",
      committees_label: "Comités",
    },
    jobs: {
      eyebrow: "Empleo",
      hero_title: "Trabajos en la CCC.",
      no_openings_prefix: "No hay puestos abiertos en este momento. Para enviar su currículum para consideración futura, por favor escriba a ",
    },
    resources: {
      eyebrow: "Recursos",
      hero_title: "Para su formación.",
      card_statements_title: "Declaraciones y Testimonio",
      card_statements_desc: "El archivo completo de declaraciones y testimonios legislativos de la CCC.",
      card_letters_title: "Cartas Obispales",
      card_letters_desc: "Cartas pastorales de los cuatro Obispos de Colorado.",
      card_voter_title: "Guía del Votante",
      card_voter_desc: "Cómo votar como católico — tema por tema.",
    },
    bishop_letters: {
      eyebrow: "Recursos",
      hero_title: "Cartas Obispales.",
      empty: "Aún no hay cartas.",
    },
    priest_signature: {
      title: "Solicitud de firma del obispo",
      signing_as_label: "Firmando como",
      author_label: "Autor",
      submit: "Solicitar firma",
      submitting: "Enviando…",
      already_completed: "Esta solicitud ya fue firmada.",
      thanks: "Gracias — su solicitud ha sido enviada.",
      generic_fail: "No pudimos enviar la solicitud. Intente de nuevo.",
    },
    send_priest: {
      title: "Enviar al Sacerdote",
      description: "Envíe los PDFs de Ayuda para la Homilía y el Inserto del Boletín de esta Alerta de Acción a un sacerdote de Colorado.",
      choose_priests: "Seleccione sacerdote(s)",
      max_10_hint: "Puede seleccionar hasta 10 sacerdotes.",
      sender_name: "Su nombre",
      sender_email: "Su correo electrónico",
      note_placeholder: "Una nota breve para el sacerdote (opcional)",
      submit: "Enviar kit",
      submitting: "Enviando…",
      thanks: "Gracias — el kit parroquial está en camino.",
      generic_fail: "No pudimos enviar el kit. Intente de nuevo.",
    },
    priest_portal: {
      title: "Preparación para la homilía — suscripción",
      subtitle: "Gestione su suscripción al digest semanal de Preparación para la homilía.",
      welcome: "Estimado",
      opt_in_label: "Enviarme el digest de Preparación para la homilía cada viernes",
      opt_in_hint: "Recibirá un correo breve que empareja cada proyecto en seguimiento con un punto de Doctrina Social Católica.",
      lang_label: "Idioma",
      lang_en: "Inglés",
      lang_es: "Español",
      save: "Guardar preferencias",
      saving: "Guardando…",
      saved: "Preferencias guardadas.",
      currently_subscribed: "Actualmente está suscrito.",
      currently_unsubscribed: "Actualmente no está suscrito.",
      link_invalid: "Este enlace no es válido o ha expirado.",
      link_invalid_body: "Por favor contacte a la oficina de la CCC para recibir un enlace nuevo.",
      generic_fail: "No pudimos guardar sus preferencias. Intente de nuevo.",
    },
    homily_archive: {
      eyebrow: "Preparación para la homilía",
      title: "Archivo del digest de Preparación para la homilía.",
      subtitle: "La Conferencia Católica de Colorado publica un digest semanal que empareja cada proyecto en seguimiento con un punto de Doctrina Social Católica. Cada semana pasada está aquí.",
      empty: "Aún no hay digests archivados.",
      bills_label: "proyectos",
      back_to_list: "Todos los digests",
      not_found: "Digest no encontrado.",
    },
  },
};

function _resolve(dict, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), dict);
}

/**
 * useT() — returns a translator bound to the current lang. Falls back to
 * English when a key is missing in `es`, and to the raw path when missing
 * in both.
 */
export function useT() {
  const { lang } = useLang();
  return (path, fallback) => {
    const primary = _resolve(STRINGS[lang] || {}, path);
    if (primary !== undefined) return primary;
    const en = _resolve(STRINGS.en, path);
    if (en !== undefined) return en;
    return fallback !== undefined ? fallback : path;
  };
}
